import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { IMC_AUTH_CONFIG } from '../auth.config';
import { firstValueFrom, filter, take, timeout, catchError, of } from 'rxjs';

export const authGuard = async () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const config = inject(IMC_AUTH_CONFIG);

  // If already authenticated, allow access
  if (authService.isAuthenticated()) {
    return true;
  }

  // Check if Azure AD redirect is being processed
  // Wait for it to complete (with timeout)
  try {
    const isProcessing = await firstValueFrom(
      authService.azureRedirectProcessing$.pipe(take(1))
    );

    if (isProcessing) {
      // Wait for redirect processing to complete
      await firstValueFrom(
        authService.azureRedirectProcessing$.pipe(
          filter(processing => !processing),
          take(1),
          timeout(10000), // 10 second timeout
          catchError(() => of(false))
        )
      );

      // Check authentication again after processing
      if (authService.isAuthenticated()) {
        return true;
      }
    }
  } catch {
    // Timeout or error, continue to login
  }

  // Check for MSAL hash in URL (Azure AD redirect in progress)
  if (window.location.hash && window.location.hash.includes('code=')) {
    // Azure AD redirect is happening, wait a moment for MSAL to process
    await new Promise(resolve => setTimeout(resolve, 500));

    if (authService.isAuthenticated()) {
      return true;
    }
  }

  router.navigate([config.loginRoute || '/login']);
  return false;
};

export const adminGuard = async () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const config = inject(IMC_AUTH_CONFIG);

  // First check if user is authenticated (using same logic as authGuard)
  if (!authService.isAuthenticated()) {
    // Wait for possible Azure AD redirect processing
    try {
      const isProcessing = await firstValueFrom(
        authService.azureRedirectProcessing$.pipe(take(1))
      );

      if (isProcessing) {
        await firstValueFrom(
          authService.azureRedirectProcessing$.pipe(
            filter(processing => !processing),
            take(1),
            timeout(10000),
            catchError(() => of(false))
          )
        );
      }
    } catch {
      // Continue to check
    }
  }

  if (authService.isAuthenticated() && authService.isAdmin()) {
    return true;
  }

  if (!authService.isAuthenticated()) {
    router.navigate([config.loginRoute || '/login']);
  } else {
    router.navigate([config.unauthorizedRoute || '/']);
  }

  return false;
};
